import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { 
  Package2, 
  Settings, 
  ArrowRight, 
  Plus, 
  Trash2, 
  Edit3, 
  Move, 
  ArrowUp, 
  ArrowDown,
  Factory,
  Layers,
  Workflow,
  Save,
  X,
  Loader2,
  ArrowLeft,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { endgoodApiService } from '../services/endgoodApi';
import { Endgood } from '../services/endgoodApi';
import { processesApiService } from '../services/processesApi';
import { Process as ApiProcess } from '../services/processesApi';
import { itemCategoryApiService, ItemCategory } from '../services/itemCategoryApi';
import { rawItemApiService, RawItem } from '../services/rawItemApi';
import { bomHeaderApiService, BomHeader, BomHeaderCreateRequest, BomHeaderUpdateRequest } from '../services/bomHeaderApi';
import { bomDetailApiService, BomDetailCreateRequest, BomDetailUpdateRequest } from '../services/bomDetailApi';
import { processFlowApiService, ProcessFlow, ProcessFlowCreateRequest, ProcessStepCreateRequest } from '../services/processFlowApi';
import { EndgoodProcessWeightDialog } from './EndgoodProcessWeightDialog';
import { formatDateTime, addYearsToString, formatDateTimeForAPI, addYears } from '../utils/dateUtils';
import { UNITS } from '../utils/constants';
import { useAuth } from '../contexts/AuthContext';

interface RawMaterialItem {
  id: string;
  bomDetailId?: number; // Optional: stores actual BOM detail ID for updates
  processName: string;
  categoryOfItem: string;
  categoryId?: number; // Store category ID for API calls
  item: string;
  itemId?: number; // Store item ID (rawItemId or endgoodId) for API calls
  quantity: number;
  unit: string;
  priority?: number;
}

interface LocalRoutingProcess {
  id: string;
  processId: string;
  sequenceOrder: number;
  processName: string;
}

// Quantity Input Component to handle decimal input properly
const QuantityInput: React.FC<{
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const [inputValue, setInputValue] = React.useState<string>(() => 
    value !== undefined && value !== null ? String(value) : ''
  );
  const [isFocused, setIsFocused] = React.useState(false);

  // Update input value when prop changes (but not while user is typing)
  React.useEffect(() => {
    if (!isFocused) {
      setInputValue(value !== undefined && value !== null ? String(value) : '');
    }
  }, [value, isFocused]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    // Allow empty string, numbers, and decimal point
    if (newValue === '' || /^-?\d*\.?\d*$/.test(newValue)) {
      setInputValue(newValue);
      // Only update parent if it's a complete number (not ending with just a decimal point)
      if (newValue === '') {
        onChange(0);
      } else if (!newValue.endsWith('.') && !newValue.endsWith('-')) {
        const numValue = parseFloat(newValue);
        if (!isNaN(numValue)) {
          onChange(numValue);
        }
      }
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    // On blur, ensure we have a valid number
    if (inputValue === '' || inputValue === '.' || inputValue === '-') {
      setInputValue('0');
      onChange(0);
    } else {
      const numValue = parseFloat(inputValue);
      if (!isNaN(numValue)) {
        setInputValue(String(numValue));
        onChange(numValue);
      } else {
        setInputValue('0');
        onChange(0);
      }
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  return (
    <Input
      type="text"
      inputMode="decimal"
      value={inputValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder="0.00"
      className="border-primary-200 w-full"
      disabled={disabled}
    />
  );
};

export function BOMPage() {
  const { bomId, mode } = useParams<{ bomId?: string; mode?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // Determine if we're in create, edit, or view mode
  const isCreateMode: boolean = !bomId && !mode;
  const isEditMode: boolean = mode === 'edit' && !!bomId;
  const isViewMode: boolean = mode === 'view' && !!bomId;
  const isEditOrViewMode: boolean = isEditMode || isViewMode;
  
  const [bomName, setBomName] = useState('');
  const [description, setDescription] = useState('');
  const [bomIsActive, setBomIsActive] = useState(true);
  const [selectedEndgood, setSelectedEndgood] = useState<string | undefined>(undefined);
  const [endgoods, setEndgoods] = useState<Endgood[]>([]);
  const [selectedEndgoodData, setSelectedEndgoodData] = useState<Endgood | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedRouting, setSelectedRouting] = useState('');
  const [selectedRoutingData, setSelectedRoutingData] = useState<ProcessFlow | null>(null);
  const [routingLoading, setRoutingLoading] = useState(false);
  const [isNewRoutingOpen, setIsNewRoutingOpen] = useState(false);
  const [isProcessWeightDialogOpen, setIsProcessWeightDialogOpen] = useState(false);
  
  // New Routing Modal State
  const [newRoutingName, setNewRoutingName] = useState('');
  const [routingProcesses, setRoutingProcesses] = useState<LocalRoutingProcess[]>([]);
  
  // New Process Modal State
  const [isNewProcessOpen, setIsNewProcessOpen] = useState(false);
  const [newProcessName, setNewProcessName] = useState('');
  const [newProcessDescription, setNewProcessDescription] = useState('');
  const [creatingProcess, setCreatingProcess] = useState(false);
  
  // Processes State
  const [processes, setProcesses] = useState<ApiProcess[]>([]);
  const [processesLoading, setProcessesLoading] = useState(false);
  
  // Process Flows State
  const [processFlows, setProcessFlows] = useState<ProcessFlow[]>([]);
  const [processFlowsLoading, setProcessFlowsLoading] = useState(false);
  
  // Item Categories and Raw Items State
  const [itemCategories, setItemCategories] = useState<ItemCategory[]>([]);
  const [rawItems, setRawItems] = useState<RawItem[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [rawItemsLoading, setRawItemsLoading] = useState(false);
  
  // Raw Materials Table State
  const [rawMaterials, setRawMaterials] = useState<RawMaterialItem[]>([]);
  
  // Existing BOM data state
  const [existingBomData, setExistingBomData] = useState<BomHeader | null>(null);
  const [existingBomDetails, setExistingBomDetails] = useState<any[]>([]);
  const [existingProcessFlow, setExistingProcessFlow] = useState<ProcessFlow | null>(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [isLoadingExistingData, setIsLoadingExistingData] = useState(false);

  // Function to load existing BOM data
  const loadExistingBomData = useCallback(async () => {
    if (!bomId || !isEditOrViewMode) return;
    
    try {
      setDataLoading(true);
      setIsLoadingExistingData(true);
      // console.log('Loading existing BOM data for ID:', bomId);
      
      // Load BOM header
      // In view/edit mode, we don't need to load all process flows or endgoods
      // since BOM information and routing are read-only
      const bomHeader = await bomHeaderApiService.getBomHeaderById(bomId);
      // console.log('Loaded BOM header:', bomHeader);
      setExistingBomData(bomHeader);
      
      // Set form values
      setBomName(bomHeader.bomName);
      setDescription(bomHeader.description);
      setBomIsActive(Boolean(bomHeader.isActive));
      
      // Set the selected endgood from BOM header (no need to load all endgoods)
      const endgood = bomHeader.endgood;
      if (endgood && endgood.id) {
        const endgoodId = endgood.id.toString();
        setSelectedEndgood(endgoodId);
        setSelectedEndgoodData(endgood);
        // Only add this endgood to the list for display purposes
        if (!endgoods.find(e => e.id === endgood.id)) {
          setEndgoods([endgood]);
        }
      } else {
        // console.warn('BOM header does not have endgood data');
        setSelectedEndgood(undefined);
        setSelectedEndgoodData(null);
      }
      
      // Load BOM details (raw materials)
      const bomDetails = await bomDetailApiService.getBomDetailsByHeader(bomHeader.id);
      // console.log('Loaded BOM details:', bomDetails);
      setExistingBomDetails(bomDetails);
      
      // In view/edit mode, we don't need all endgoods since BOM info is read-only
      // Use the endgood from BOM header if available
      const currentEndgoods = bomHeader.endgood ? [bomHeader.endgood] : [];
      
      // Convert BOM details to raw materials format
      const convertedRawMaterials = bomDetails.map((detail, index) => {
        let processName = detail.process?.processName || '';

        // Determine item name - could be from rawItem or endgood (if category is Internal Manufacturing Part)
        let itemName = 'Unknown Item';
        let itemId: number | undefined;
        
        // Get category name
        const categoryName = detail.rawCategory?.categoryName || detail.rawItem?.itemCategory?.categoryName || '';
        const isInternalCategory = isInternalManufacturingParts(categoryName);
        
        // If category is Internal Manufacturing Part, prioritize endgood
        if (isInternalCategory) {
          if (detail.endgood) {
            // For Internal Manufacturing Part, use endgood if available in response
            itemName = detail.endgood.endgoodName;
            itemId = detail.endgood.id;
          } else if (detail.endgoodId) {
            // If endgood object is not loaded but endgoodId exists, look it up from the endgoods list
            const foundEndgood = currentEndgoods.find(e => e.id === detail.endgoodId);
            if (foundEndgood) {
              itemName = foundEndgood.endgoodName;
              itemId = foundEndgood.id;
            } else {
              // If not found in list, use placeholder (shouldn't happen normally)
              itemName = `Endgood #${detail.endgoodId}`;
              itemId = detail.endgoodId;
            }
          } else if (detail.rawItem) {
            // Fallback: if endgood is not available but rawItem exists, use rawItem
            // This handles edge cases where the API might not have loaded the endgood object
            itemName = detail.rawItem.itemName;
            itemId = detail.rawItem.id;
          }
        } else {
          // For other categories, use rawItem
          if (detail.rawItem) {
            itemName = detail.rawItem.itemName;
            itemId = detail.rawItem.id;
          }
        }

        // Determine unit - use 'pcs' for endgoods, otherwise use stored unit or raw item unit
        let unit = detail.unit || 'kg';
        if (isInternalCategory) {
          unit = detail.unit || 'pcs'; // Default to 'pcs' for endgoods
        } else if (detail.rawItem?.itemUnit) {
          unit = detail.unit || detail.rawItem.itemUnit;
        }

        const converted = {
          id: detail.id.toString(), // Use actual BOM detail ID for updates
          bomDetailId: detail.id, // Store the actual BOM detail ID
          processName: processName,
          categoryOfItem: categoryName || 'Raw Material',
          categoryId: detail.rawCategoryId || detail.rawItem?.itemCategory?.id,
          item: itemName,
          itemId: itemId,
          quantity: detail.quantity || 0,
          unit: unit,
          priority: detail.priority
        };
        // console.log('Converted raw material:', converted);
        return converted;
      });
      // console.log('Setting raw materials:', convertedRawMaterials);
      
      // Sort raw materials by process name (ascending) and priority (ascending) for view mode
      const sortedRawMaterials = (mode === 'view')
        ? [...convertedRawMaterials].sort((a, b) => {
            // First sort by process name (ascending)
            const processCompare = (a.processName || '').localeCompare(b.processName || '');
            if (processCompare !== 0) return processCompare;
            
            // If process names are equal, sort by priority (ascending)
            const priorityA = a.priority ?? 0;
            const priorityB = b.priority ?? 0;
            return priorityA - priorityB;
          })
        : convertedRawMaterials;
      
      setRawMaterials(sortedRawMaterials);
      
      // Debug: Check if raw materials were set
      setTimeout(() => {
        // console.log('Raw materials state after setting:', rawMaterials);
      }, 100);
      
      // Load process flow if linked to this BOM
      if (bomHeader.processFlow) {
        try {
          // console.log('Loading linked process flow:', bomHeader.processFlow);
          setSelectedRouting(bomHeader.processFlow.id.toString());
          
          // Fetch complete process flow data with process steps
          await fetchRoutingWithSteps(bomHeader.processFlow.id.toString());
        } catch (error) {
          // console.error('Error loading linked process flow:', error);
          // Continue without process flow
        }
      } else {
        // console.log('No process flow linked to this BOM');
      }
      
    } catch (error) {
      // console.error('Error loading existing BOM data:', error);
      toast.error('Failed to load BOM data');
    } finally {
      setDataLoading(false);
      setIsLoadingExistingData(false);
    }
  }, [bomId, isEditOrViewMode]);

  // Function to fetch complete routing data with process steps
  const fetchRoutingWithSteps = useCallback(async (routingId: string) => {
    try {
      setRoutingLoading(true);
      // console.log('Fetching routing with steps for ID:', routingId);
      
      // First, fetch the ProcessFlow
      const routingData = await processFlowApiService.getProcessFlowById(routingId);
      // console.log('Fetched ProcessFlow:', routingData);
      
      // Then, fetch the ProcessSteps for this specific ProcessFlow
      const routingProcessSteps = await processFlowApiService.getProcessStepsByProcessFlow(routingData.id);
      // console.log('ProcessSteps for this routing:', routingProcessSteps);
      
      // Combine ProcessFlow with its ProcessSteps
      const completeRoutingData: ProcessFlow = {
        ...routingData,
        processSteps: routingProcessSteps
      };
      
      // console.log('Complete routing data with steps:', completeRoutingData);
      setSelectedRoutingData(completeRoutingData);
      
    } catch (error) {
      // console.error('Error fetching routing with steps:', error);
      toast.error('Failed to fetch routing details');
      setSelectedRoutingData(null);
    } finally {
      setRoutingLoading(false);
    }
  }, []);

  // Function to refresh process flows
  const refreshProcessFlows = useCallback(async () => {
    try {
      setProcessFlowsLoading(true);
      const processFlowsData = await processFlowApiService.getProcessFlows();
      setProcessFlows(processFlowsData);
    } catch (error) {
      // console.error('Error refreshing process flows:', error);
      toast.error('Failed to refresh process flows');
    } finally {
      setProcessFlowsLoading(false);
    }
  }, []);



  // Fetch endgoods, processes, process flows, item categories, and raw items from API
  // Skip in view/edit mode since users can only add new BOM details (not change BOM info or routing)
  useEffect(() => {
    // In view/edit mode, skip fetching endgoods, processes, and process flows
    // Only fetch item categories and raw items in edit mode (needed for adding new BOM details)
    if (isViewMode) {
      // View mode: No data needed since everything is read-only
      return;
    }

    let isMounted = true;
    
    const fetchData = async () => {
      try {
        setLoading(true);
        setProcessesLoading(true);
        setProcessFlowsLoading(true);
        setCategoriesLoading(true);
        setRawItemsLoading(true);
        
        // Only fetch endgoods, processes, and process flows in create mode
        if (isCreateMode) {
          // Fetch endgoods
          const endgoodsResponse = await endgoodApiService.getEndgoodsIdName();
          if (isMounted && endgoodsResponse.success && Array.isArray(endgoodsResponse.data)) {
            setEndgoods(endgoodsResponse.data);
          }
          
          // Fetch processes
          const processesData = await processesApiService.getProcesses();
          if (isMounted) setProcesses(processesData);
          
          // Fetch process flows
          const processFlowsData = await processFlowApiService.getProcessFlows();
          if (isMounted) setProcessFlows(processFlowsData);
        }
        
        // Fetch item categories (needed for adding new BOM details in edit mode)
        const categoriesData = await itemCategoryApiService.getItemCategories();
        if (isMounted) setItemCategories(categoriesData);
        
        // Fetch raw items (needed for adding new BOM details in edit mode)
        const rawItemsData = await rawItemApiService.getRawItems();
        if (isMounted) setRawItems(rawItemsData);

        // In edit mode, also fetch endgoods (needed for internal manufacturing parts category)
        if (isEditMode) {
          const endgoodsResponse = await endgoodApiService.getEndgoodsIdName();
          if (isMounted && endgoodsResponse.success && Array.isArray(endgoodsResponse.data)) {
            setEndgoods(endgoodsResponse.data);
          }
        }
      } catch (error) {
        // console.error('Error fetching data:', error);
        if (isMounted) toast.error('Failed to fetch data');
      } finally {
        if (isMounted) {
          setLoading(false);
          setProcessesLoading(false);
          setProcessFlowsLoading(false);
          setCategoriesLoading(false);
          setRawItemsLoading(false);
        }
      }
    };

    fetchData();
    
    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, [isCreateMode, isEditMode, isViewMode]);

  // Load existing BOM data when in edit or view mode
  useEffect(() => {
    if (isEditOrViewMode && bomId) {
      // console.log('Loading existing BOM data for edit/view mode');
      loadExistingBomData();
    }
  }, [isEditOrViewMode, bomId, loadExistingBomData]);

  // Cleanup function for component unmount
  useEffect(() => {
    return () => {
      // Clear all state when component unmounts
      setSelectedRouting('');
      setSelectedRoutingData(null);
      setSelectedEndgood(undefined);
      setSelectedEndgoodData(null);
      setRawMaterials([]);
      setRoutingProcesses([]);
    };
  }, []);

  // Clear routing data when routing selection changes
  useEffect(() => {
    if (!selectedRouting) {
      setSelectedRoutingData(null);
    }
  }, [selectedRouting]);

  // Helper function to check if category is "Internal Manufacturing Part" or "Internal Manufactured Parts"
  const isInternalManufacturingParts = (categoryName: string) => {
    if (!categoryName) return false;
    const lowerName = categoryName.toLowerCase().trim();
    // Check for exact matches: "internal manufacturing parts" or "internal manufactured parts"
    return lowerName === 'internal manufacturing parts' || 
           lowerName === 'internal manufactured parts' ||
           lowerName === 'internal manufacturing part' ||
           lowerName === 'internal manufactured part';
  };

  const addRawMaterialRow = () => {
    const newRow: RawMaterialItem = {
      id: Date.now().toString(),
      processName: '',
      categoryOfItem: '',
      item: '',
      quantity: 0,
      unit: '',
      priority: undefined
    };
    setRawMaterials([...rawMaterials, newRow]);
  };

  const updateRawMaterial = (id: string, field: keyof RawMaterialItem, value: string | number | undefined) => {
    setRawMaterials(prev => prev.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        
        // If category changes, clear the item to ensure proper filtering and set categoryId
        if (field === 'categoryOfItem') {
          updatedItem.item = '';
          updatedItem.itemId = undefined; // Clear item ID when category changes
          updatedItem.unit = ''; // Clear unit when category changes
          // Find and set the category ID (by id from "category-{id}" or by name, case-insensitive)
          const valueStr = String(value ?? '');
          const idMatch = valueStr.match(/^category-(\d+)$/);
          let selectedCategory = idMatch
            ? itemCategories.find(cat => cat.id === parseInt(idMatch[1], 10))
            : itemCategories.find(cat => (cat.categoryName || '').trim() === valueStr);
          if (!selectedCategory && valueStr) {
            const nameLower = valueStr.trim().toLowerCase();
            selectedCategory = itemCategories.find(cat =>
              (cat.categoryName || '').trim().toLowerCase() === nameLower
            );
          }
          if (selectedCategory) {
            updatedItem.categoryId = selectedCategory.id;
          }
          console.log('Category changed:', {
            newCategory: value,
            isInternal: isInternalManufacturingParts(value as string),
            endgoodsCount: endgoods.length
          });
        }
        
        // If item changes, auto-populate the unit from the selected raw item or endgood.
        // We prefer selecting by ID (string) from the dropdown to avoid name/whitespace mismatches.
        if (field === 'itemId') {
          const selectedId = typeof value === 'string' ? Number(value) : (value as number | undefined);
          if (selectedId && !Number.isNaN(selectedId)) {
            if (isInternalManufacturingParts(updatedItem.categoryOfItem)) {
              const selectedEndgood = endgoods.find(e => e.id === selectedId);
              if (selectedEndgood) {
                updatedItem.itemId = selectedEndgood.id;
                updatedItem.item = selectedEndgood.endgoodName;
                updatedItem.unit = 'pcs';
              }
            } else {
              const selectedRawItem = rawItems.find(r => r.id === selectedId);
              if (selectedRawItem) {
                updatedItem.itemId = selectedRawItem.id;
                updatedItem.item = selectedRawItem.itemName;
                updatedItem.unit = selectedRawItem.itemUnit;
              }
            }
          }
        }
        
        return updatedItem;
      }
      return item;
    }));
  };



  const saveNewBomDetail = async (materialId: string) => {
    const material = rawMaterials.find(m => m.id === materialId);
    if (!material) {
      toast.error('Material not found');
      return;
    }

    // Only allow saving new rows (without bomDetailId) in edit mode
    if (material.bomDetailId) {
      toast.error('This entry is already saved');
      return;
    }

    if (!isEditMode || !existingBomData) {
      toast.error('Cannot save: BOM header not found');
      return;
    }

    try {
      setLoading(true);
      // console.log('Saving new BOM detail:', material);

      // Validate required fields
      if (!material.processName) {
        toast.error('Please select a process');
        return;
      }
      if (!material.categoryOfItem) {
        toast.error('Please select a category');
        return;
      }
      if (!material.item) {
        toast.error('Please select an item');
        return;
      }
      if (!material.quantity || material.quantity <= 0) {
        toast.error('Please enter a valid quantity');
        return;
      }
      if (!material.unit) {
        toast.error('Unit is required');
        return;
      }

      // Find category ID: use stored categoryId, or match by id from "category-{id}" value, or by name (case-insensitive)
      let category = material.categoryId != null
        ? itemCategories.find(cat => cat.id === material.categoryId)
        : undefined;
      if (!category && material.categoryOfItem) {
        const categoryIdMatch = String(material.categoryOfItem).match(/^category-(\d+)$/);
        if (categoryIdMatch) {
          const id = parseInt(categoryIdMatch[1], 10);
          category = itemCategories.find(cat => cat.id === id);
        }
      }
      if (!category && material.categoryOfItem) {
        const name = (material.categoryOfItem || '').trim().toLowerCase();
        category = itemCategories.find(cat =>
          (cat.categoryName || '').trim().toLowerCase() === name
        );
      }
      if (!category) {
        toast.error('Category not found');
        return;
      }
      const categoryId = category.id;

      // Determine if it's a raw item or endgood
      const isInternalCategory = isInternalManufacturingParts(material.categoryOfItem);
      let rawItemId: number | undefined;
      let endgoodId: number | undefined;

      if (isInternalCategory) {
        const selectedEndgood = endgoods.find(e => e.endgoodName === material.item);
        if (!selectedEndgood) {
          toast.error('Endgood not found');
          return;
        }
        endgoodId = selectedEndgood.id;
      } else {
        const selectedRawItem = rawItems.find(r => r.itemName === material.item);
        if (!selectedRawItem) {
          toast.error('Raw item not found');
          return;
        }
        rawItemId = selectedRawItem.id;
      }

      // Find process ID from routing
      let processId = 1; // Default fallback
      if (selectedRoutingData && selectedRoutingData.processSteps) {
        const processStep = selectedRoutingData.processSteps.find(step => 
          step.process.processName === material.processName
        );
        if (processStep) {
          processId = processStep.process.id;
        }
      }

      const bomDetailData: BomDetailCreateRequest = {
        quantity: material.quantity,
        unit: material.unit,
        unitCost: 0,
        totalCost: 0,
        wastagePercentage: 0,
        wastageQuantity: 0,
        totalQuantity: material.quantity,
        remarks: `Process: ${material.processName}, Category: ${material.categoryOfItem}`,
        isCritical: false,
        leadTimeDays: 0,
        priority: material.priority,
        branchId: existingBomData.branchId || 1,
        bomHeaderId: existingBomData.id,
        rawItemId: rawItemId,
        endgoodId: endgoodId,
        rawCategoryId: categoryId,
        processId: processId,
        createdBy: user?.id || 1,
        updatedBy: user?.id || 1
      };

      // console.log('Creating BOM Detail:', bomDetailData);
      const createdBomDetail = await bomDetailApiService.createBomDetail(bomDetailData);
      
      // Update the material with the new bomDetailId
      setRawMaterials(prev => prev.map(m => 
        m.id === materialId 
          ? { ...m, bomDetailId: createdBomDetail.id }
          : m
      ));

      // Add to existing BOM details
      setExistingBomDetails(prev => [...prev, createdBomDetail]);

      toast.success('BOM detail saved successfully');
    } catch (error) {
      // console.error('Error saving BOM detail:', error);
      toast.error('Failed to save BOM detail');
    } finally {
      setLoading(false);
    }
  };

  const saveBomDetail = async (materialId: string) => {
    const material = rawMaterials.find(m => m.id === materialId);
    if (!material || !material.bomDetailId) {
      toast.error('Cannot save: BOM detail not found');
      return;
    }

    try {
      // console.log('Saving BOM detail:', material);
      
      // Get the current BOM detail to update
      const currentBomDetail = existingBomDetails.find(detail => detail.id === material.bomDetailId);
      if (!currentBomDetail) {
        // console.error('BOM detail not found for update:', material.bomDetailId);
        return;
      }
      
      // Debug: Log the current BOM detail structure
      // console.log('Current BOM detail structure:', currentBomDetail);
      // console.log('BOM Header:', currentBomDetail.bomHeader);
      // console.log('Raw Item:', currentBomDetail.rawItem);
      
      // Check if required nested objects exist
      if (!currentBomDetail.bomHeader) {
        // console.error('BOM Header is undefined for BOM detail:', currentBomDetail.id);
        toast.error('Cannot save: BOM header information is missing');
        return;
      }
      
      // Check if we have either rawItem or endgood
      const isInternalCategory = isInternalManufacturingParts(material.categoryOfItem);
      if (isInternalCategory && !currentBomDetail.endgood && !material.itemId) {
        // console.error('Endgood is undefined for BOM detail:', currentBomDetail.id);
        toast.error('Cannot save: Endgood information is missing');
        return;
      } else if (!isInternalCategory && !currentBomDetail.rawItem && !material.itemId) {
        // console.error('Raw Item is undefined for BOM detail:', currentBomDetail.id);
        toast.error('Cannot save: Raw item information is missing');
        return;
      }
      
      // Find the process ID from the selected routing
      let processId = currentBomDetail.process?.id || 1; // Use existing process ID or default fallback
      if (selectedRoutingData && selectedRoutingData.processSteps) {
        const processStep = selectedRoutingData.processSteps.find(step => 
          step.process.processName === material.processName
        );
        if (processStep) {
          processId = processStep.process.id;
        }
      }

      // Find the category ID
      const categoryId = material.categoryId || itemCategories.find(cat => cat.categoryName === material.categoryOfItem)?.id || currentBomDetail.rawCategoryId;
      
      // Determine rawItemId and endgoodId based on category
      let rawItemId: number | undefined;
      let endgoodId: number | undefined;
      
      if (isInternalManufacturingParts(material.categoryOfItem)) {
        // For "Internal Manufacturing Part", find the endgood
        const endgood = endgoods.find(e => e.endgoodName === material.item) ||
                       (material.itemId ? endgoods.find(e => e.id === material.itemId) : null);
        if (!endgood) {
          toast.error(`Endgood not found: ${material.item}`);
          return;
        }
        endgoodId = endgood.id;
      } else {
        // For other categories, use the existing rawItem or find by name
        if (currentBomDetail.rawItem) {
          rawItemId = currentBomDetail.rawItem.id;
        } else {
          const rawItem = rawItems.find(item => item.itemName === material.item) ||
                         (material.itemId ? rawItems.find(item => item.id === material.itemId) : null);
          if (!rawItem) {
            toast.error(`Raw item not found: ${material.item}`);
            return;
          }
          rawItemId = rawItem.id;
        }
      }
      
      // Create update data with the current material values
      const updateData = {
        id: currentBomDetail.id,
        quantity: material.quantity,
        unit: material.unit,
        unitCost: currentBomDetail.unitCost || 0,
        totalCost: currentBomDetail.totalCost || 0,
        wastagePercentage: currentBomDetail.wastagePercentage || 0,
        wastageQuantity: currentBomDetail.wastageQuantity || 0,
        totalQuantity: currentBomDetail.totalQuantity || 0,
        remarks: currentBomDetail.remarks || '',
        isCritical: currentBomDetail.isCritical || false,
        leadTimeDays: currentBomDetail.leadTimeDays || 0,
        priority: material.priority,
        branchId: currentBomDetail.branchId || 1,
        bomHeaderId: currentBomDetail.bomHeader.id,
        rawItemId: rawItemId, // For raw items
        endgoodId: endgoodId, // For internal manufacturing parts (endgoods)
        rawCategoryId: categoryId, // Include raw category ID
        processId: processId, // Include process ID in update
        createdBy: currentBomDetail.createdBy || user?.id || 1,
        updatedBy: user?.id || 1
      };
      
      // console.log('Update data being sent:', updateData);
      
      // Update via API
      const result = await bomDetailApiService.updateBomDetail(material.bomDetailId.toString(), updateData);
      // console.log('BOM detail saved successfully:', result);
      toast.success('BOM detail saved successfully');
      
    } catch (error) {
      // console.error('Error saving BOM detail:', error);
      toast.error('Failed to save BOM detail');
    }
  };

  const removeRawMaterial = (id: string) => {
    setRawMaterials(prev => prev.filter(item => item.id !== id));
  };

  const addProcessToRouting = () => {
    const newProcess: LocalRoutingProcess = {
      id: Date.now().toString(),
      processId: '',
      sequenceOrder: routingProcesses.length + 1,
      processName: ''
    };
    setRoutingProcesses([...routingProcesses, newProcess]);
  };

  const updateRoutingProcess = (id: string, field: keyof LocalRoutingProcess, value: string | number) => {
    setRoutingProcesses(prev => prev.map(process => {
      if (process.id === id) {
        const updatedProcess = { ...process, [field]: value };
        
        // If processId changes, update the processName
        if (field === 'processId') {
          const selectedProcess = processes.find(p => p.id.toString() === value);
          updatedProcess.processName = selectedProcess?.processName || '';
        }
        
        return updatedProcess;
      }
      return process;
    }));
  };

  const removeProcessFromRouting = (id: string) => {
    setRoutingProcesses(prev => prev.filter(process => process.id !== id));
  };

  const saveNewProcess = async () => {
    if (!newProcessName.trim()) {
      toast.error('Please enter a process name');
      return;
    }
    
    try {
      setCreatingProcess(true);
      const processData = {
        processName: newProcessName.trim(),
        description: newProcessDescription.trim(),
        workAreaId: 1, // TODO: Get from context or user selection
        isActive: true,
        createdBy: user?.id || 1,
        updatedBy: user?.id || 1
      };
      
      // Create process via API
      const newProcess = await processesApiService.createProcess(processData);
      
      // Add to local state
      setProcesses(prev => [...prev, newProcess]);
      
      toast.success('New process created successfully');
      setIsNewProcessOpen(false);
      setNewProcessName('');
      setNewProcessDescription('');
    } catch (error) {
      // console.error('Error creating process:', error);
      toast.error('Failed to create process');
    } finally {
      setCreatingProcess(false);
    }
  };

  const saveNewRouting = async () => {
    if (!newRoutingName.trim()) {
      toast.error('Please enter a routing name');
      return;
    }
    if (routingProcesses.length === 0) {
      toast.error('Please add at least one process');
      return;
    }
    
    try {
      setLoading(true);
      
      // Create Process Flow
      const processFlowData: ProcessFlowCreateRequest = {
        name: newRoutingName, // Use the routing name as the process flow name
        processFlowNumber: `PF-${Date.now()}`, // Generate unique process flow number
        version: '1.0',
        status: 'Draft',
        description: newRoutingName,
        totalCycleTime: 0, // Will be calculated from process steps
        setupTime: 0,
        runTime: 0,
        totalLaborHours: 0,
        machineHours: 0,
        qualityCheckPoints: 0,
        isActive: true,
        branchId: existingBomData?.branchId || 1,
        createdBy: user?.id || 1,
        updatedBy: user?.id || 1
      };
      
      const processFlow = await processFlowApiService.createProcessFlow(processFlowData);
      
      if (!processFlow || !processFlow.id) {
        throw new Error('Failed to create process flow - no ID returned');
      }
      
      // console.log('ProcessFlow created successfully:', processFlow);
      
      // Create Process Steps for the Process Flow
      const processStepPromises = routingProcesses.map(async (process) => {
        if (!process.processId) {
          throw new Error(`Process ID is missing for process: ${process.processName}`);
        }
        
        const processStepData: ProcessStepCreateRequest = {
          sequenceOrder: process.sequenceOrder,
          cycleTime: 0, // Default value
          setupTime: 0,
          runTime: 0,
          laborHours: 0,
          machineHours: 0,
          temperature: 0,
          pressure: 0,
          speed: 0,
          feedRate: 0,
          isQualityCheck: false,
          qualityParameters: '',
          toleranceMin: 0,
          toleranceMax: 0,
          isCritical: false,
          remarks: `Process: ${process.processName}`,
          branchId: existingBomData?.branchId || 1,
          processFlowId: processFlow.id, // Use the process flow ID
          processId: parseInt(process.processId),
          createdBy: user?.id || 1,
          updatedBy: user?.id || 1
        };
        
        // console.log('Creating ProcessStep:', processStepData);
        return await processFlowApiService.createProcessStep(processStepData);
      });
      
      const createdProcessSteps = await Promise.all(processStepPromises);
      
      if (createdProcessSteps.length !== routingProcesses.length) {
        throw new Error('Some process steps failed to create');
      }
      
      // console.log('All ProcessSteps created successfully:', createdProcessSteps);
      
      // Refresh process flows
      await refreshProcessFlows();
      
      toast.success('New routing created successfully');
      setIsNewRoutingOpen(false);
      setNewRoutingName('');
      setRoutingProcesses([]);
    } catch (error) {
      // console.error('Error creating routing:', error);
      toast.error('Failed to create routing');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setBomName('');
    setDescription('');
    setBomIsActive(true);
    setSelectedEndgood(undefined);
    setSelectedEndgoodData(null);
    setSelectedRouting('');
    setRawMaterials([]);
    setNewRoutingName('');
    setRoutingProcesses([]);
    toast.info('Form reset');
  };

  // Edit mode: update only active/inactive flag; keep all other payload fields unchanged.
  const handleSaveBomStatus = async () => {
    if (!isEditMode || !existingBomData) return;
    try {
      setLoading(true);
      const processFlowIdFromExisting =
        existingBomData.processFlowId ?? existingBomData.processFlow?.id;

      const statusOnlyPayload: BomHeaderUpdateRequest = {
        id: existingBomData.id,
        bomNumber: existingBomData.bomNumber,
        bomName: existingBomData.bomName,
        version: existingBomData.version,
        status: existingBomData.status,
        effectiveDate: existingBomData.effectiveDate,
        expiryDate: existingBomData.expiryDate,
        description: existingBomData.description,
        totalCost: existingBomData.totalCost,
        laborCost: existingBomData.laborCost,
        overheadCost: existingBomData.overheadCost,
        materialCost: existingBomData.materialCost,
        productionQuantity: existingBomData.productionQuantity,
        unitCost: existingBomData.unitCost,
        approvedBy: existingBomData.approvedBy,
        approvedDate: existingBomData.approvedDate,
        isActive: bomIsActive,
        branchId: existingBomData.branchId,
        processFlowId: processFlowIdFromExisting,
        createdBy: existingBomData.createdBy,
        updatedBy: user?.id || existingBomData.updatedBy || 1,
        endgoodId:
          existingBomData.endgoodId ??
          existingBomData.endgood?.id ??
          (selectedEndgood ? parseInt(selectedEndgood, 10) : 0),
      };

      await bomHeaderApiService.updateBomHeader(existingBomData.id.toString(), statusOnlyPayload);
      setExistingBomData((prev) => (prev ? { ...prev, isActive: bomIsActive } : prev));
      toast.success('BOM status updated successfully!');
    } catch (error) {
      toast.error('Failed to update BOM status');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!bomName.trim()) {
      toast.error('Please enter BOM name');
      return;
    }
    if (!selectedEndgood || selectedEndgood.trim() === '') {
      toast.error('Please select an endgood');
      return;
    }
    if (!selectedRouting) {
      toast.error('Please select a routing');
      return;
    }
    
    try {
      setLoading(true);
      
      // Validate required fields
      if (!selectedEndgood) {
        toast.error('Please select an end good');
        return;
      }

      if (!bomName.trim()) {
        toast.error('Please enter a BOM name');
        return;
      }

      // Get the selected routing data
      const routingData = processFlows.find(r => r.id.toString() === selectedRouting);
      if (!routingData) {
        toast.error('Selected routing not found');
        return;
      }

      if (isEditMode && existingBomData) {
        // Update existing BOM
        const bomHeaderUpdateData: BomHeaderUpdateRequest = {
          id: existingBomData.id,
          bomNumber: existingBomData.bomNumber,
          bomName: bomName,
          version: existingBomData.version,
          status: existingBomData.status,
          effectiveDate: existingBomData.effectiveDate,
          expiryDate: existingBomData.expiryDate,
          description: description,
          totalCost: existingBomData.totalCost,
          laborCost: existingBomData.laborCost,
          overheadCost: existingBomData.overheadCost,
          materialCost: existingBomData.materialCost,
          productionQuantity: existingBomData.productionQuantity,
          unitCost: existingBomData.unitCost,
          approvedBy: existingBomData.approvedBy,
          approvedDate: existingBomData.approvedDate,
          isActive: bomIsActive,
          branchId: existingBomData.branchId,
          createdBy: existingBomData.createdBy,
          updatedBy: user?.id || 1,
          endgoodId: parseInt(selectedEndgood)
        };

        // If a routing is selected, update the process flow link
        if (selectedRouting && selectedRouting.trim() !== '') {
          bomHeaderUpdateData.processFlowId = parseInt(selectedRouting);
        } else {
          bomHeaderUpdateData.processFlowId = undefined;
        }

        const updatedBomHeader = await bomHeaderApiService.updateBomHeader(existingBomData.id.toString(), bomHeaderUpdateData);
        toast.success('BOM updated successfully!');
        
        // Navigate back to listing
        navigate('/bom');
        return;
      }

      // Create new BOM (existing logic)
      // console.log('Creating BOM with endgoodId:', selectedEndgood);
      // console.log('Selected endgood data:', selectedEndgoodData);
      
      // 1. Create BOM Header first
      const bomHeaderData: BomHeaderCreateRequest = {
        bomNumber: `BOM-${Date.now()}`, // Generate unique BOM number
        bomName: bomName,
        version: '1.0',
        status: 'Draft',
        effectiveDate: formatDateTimeForAPI(new Date()),
        expiryDate: formatDateTimeForAPI(addYears(new Date(), 1)), // 1 year from now
        description: description,
        totalCost: 0, // Will be calculated from BOM details
        laborCost: 0,
        overheadCost: 0,
        materialCost: 0,
        productionQuantity: 1,
        unitCost: 0,
        approvedBy: '',
        approvedDate: formatDateTimeForAPI(new Date()),
        isActive: bomIsActive,
        branchId: existingBomData?.branchId || 1,
        processFlowId: selectedRouting ? parseInt(selectedRouting) : undefined, // Link to selected process flow
        createdBy: user?.id || 1,
        updatedBy: user?.id || 1,
        endgoodId: parseInt(selectedEndgood)
      };

      // Ensure endgoodId is a valid number
      if (isNaN(bomHeaderData.endgoodId)) {
        toast.error('Invalid end good ID');
        return;
      }

      // console.log('BOM Header data being sent:', bomHeaderData);
      // console.log('endgoodId being sent:', bomHeaderData.endgoodId);
      // console.log('endgoodId type:', typeof bomHeaderData.endgoodId);
      
      const bomHeader = await bomHeaderApiService.createBomHeader(bomHeaderData);
      
      if (!bomHeader || !bomHeader.id) {
        throw new Error('Failed to create BOM header - no ID returned');
      }
      
      // console.log('BOM Header created successfully:', bomHeader);
      // console.log('Created BOM header endgood:', bomHeader.endgood);
      
      // Note: Process flow is now linked via processFlowId in the BOM header creation
      // No need to create new process flows or process steps - we link to existing ones
      
      // 4. Create BOM Details (Raw Materials) - Only if user provided raw materials
      if (rawMaterials.length > 0) {
        try {
          const bomDetailPromises = rawMaterials.map(async (material) => {
            // Find the category ID
            const categoryId = material.categoryId || itemCategories.find(cat => cat.categoryName === material.categoryOfItem)?.id;
            if (!categoryId) {
              // console.warn(`Category ID not found for: ${material.categoryOfItem}, skipping this material`);
              return null; // Skip this material if category ID is missing
            }
            
            // Determine if this is an endgood or raw item based on category
            let rawItemId: number | undefined;
            let endgoodId: number | undefined;
            let unit: string;
            
            if (isInternalManufacturingParts(material.categoryOfItem)) {
              // For "Internal Manufacturing Part", find the endgood
              const endgood = endgoods.find(e => e.endgoodName === material.item) || 
                             (material.itemId ? endgoods.find(e => e.id === material.itemId) : null);
              if (!endgood) {
                // console.warn(`Endgood not found: ${material.item}, skipping this material`);
                return null;
              }
              // Use endgoodId for internal manufacturing parts
              endgoodId = endgood.id;
              unit = material.unit || 'pcs'; // Use 'pcs' for endgoods
            } else {
              // For other categories, find the raw item
              const rawItem = rawItems.find(item => item.itemName === material.item) ||
                             (material.itemId ? rawItems.find(item => item.id === material.itemId) : null);
              if (!rawItem) {
                // console.warn(`Raw item not found: ${material.item}, skipping this material`);
                return null;
              }
              rawItemId = rawItem.id;
              unit = material.unit || rawItem.itemUnit;
            }
            
            // Find the process ID from the selected routing
            let processId = 1; // Default fallback
            if (selectedRoutingData && selectedRoutingData.processSteps) {
              const processStep = selectedRoutingData.processSteps.find(step => 
                step.process.processName === material.processName
              );
              if (processStep) {
                processId = processStep.process.id;
              }
            }

            const bomDetailData: BomDetailCreateRequest = {
              quantity: material.quantity,
              unit: unit,
              unitCost: 0, // Default value, should be fetched from raw item
              totalCost: 0, // Will be calculated
              wastagePercentage: 0, // Default value
              wastageQuantity: 0, // Will be calculated
              totalQuantity: material.quantity, // Same as quantity for now
              remarks: `Process: ${material.processName}, Category: ${material.categoryOfItem}`,
              isCritical: false, // Default value
              leadTimeDays: 0, // Default value
              priority: material.priority,
              branchId: existingBomData?.branchId || 1,
              bomHeaderId: bomHeader.id, // Link to the created BOM Header
              rawItemId: rawItemId, // For raw items
              endgoodId: endgoodId, // For internal manufacturing parts (endgoods)
              rawCategoryId: categoryId, // Include raw category ID
              processId: processId, // Link to the process from the routing
              createdBy: user?.id || 1,
              updatedBy: user?.id || 1
            };
            
            // console.log('Creating BOM Detail:', bomDetailData);
            return await bomDetailApiService.createBomDetail(bomDetailData);
          });
          
          const createdBomDetails = await Promise.all(bomDetailPromises);
          const successfulDetails = createdBomDetails.filter(detail => detail !== null);
          
          // console.log('BOM Details created successfully:', successfulDetails);
          
          if (successfulDetails.length < rawMaterials.length) {
            const skippedCount = rawMaterials.length - successfulDetails.length;
            toast.warning(`BOM created successfully, but ${skippedCount} raw material(s) were skipped due to missing items`);
          }
        } catch (bomDetailError) {
          // console.error('Error creating BOM details:', bomDetailError);
          toast.warning('BOM created successfully, but some raw materials could not be added');
        }
      } else {
        // console.log('No raw materials provided, skipping BOM details creation');
      }
      
      toast.success('BOM saved successfully!');
      
      // Refresh process flows to ensure latest data
      await refreshProcessFlows();
      
      // Navigate back to BOM listing page
      navigate('/bom');
      
      // Reset form
      setBomName('');
      setDescription('');
      setSelectedEndgood(undefined);
      setSelectedEndgoodData(null);
      setSelectedRouting('');
      setSelectedRoutingData(null);
      setRawMaterials([]);
      
    } catch (error) {
      // console.error('Error saving BOM:', error);
      toast.error('Failed to save BOM');
    } finally {
      setLoading(false);
    }
  };

  const ProcessFlowDiagram = ({ processFlow }: { processFlow: ProcessFlow }) => {
    // console.log('ProcessFlowDiagram received processFlow:', processFlow);
    // console.log('ProcessFlow processSteps:', processFlow.processSteps);
    // console.log('ProcessFlow ID:', processFlow.id);
    // console.log('ProcessFlow Number:', processFlow.processFlowNumber);
    
    if (!processFlow.processSteps || processFlow.processSteps.length === 0) {
      return (
        <div className="p-4 bg-gradient-to-r from-primary-50/30 to-primary-100/30 rounded-lg border border-primary-200">
          <div className="flex items-center gap-2 mb-4">
            <Workflow className="h-5 w-5 text-primary-600" />
            <h4 className="font-medium text-primary-800">Process Flow</h4>
          </div>
          <div className="flex items-center justify-center p-8">
            <span className="text-primary-600">No process steps found for this routing</span>
          </div>
        </div>
      );
    }
    
    return (
      <div className="p-4 bg-gradient-to-r from-primary-50/30 to-primary-100/30 rounded-lg border border-primary-200">
        <div className="flex items-center gap-2 mb-4">
          <Workflow className="h-5 w-5 text-primary-600" />
          <h4 className="font-medium text-primary-800">Process Flow</h4>
        </div>
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {processFlow.processSteps
            .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
            .filter((processStep, index, array) => 
              array.findIndex(step => step.id === processStep.id) === index
            )
            .map((processStep, index) => (
              <React.Fragment key={processStep.id}>
                <div className="flex-shrink-0">
                  <div className="bg-white border-2 border-primary-300 rounded-lg p-3 min-w-[120px] text-center">
                    <div className="text-sm font-medium text-primary-800">{processStep.process.processName}</div>
                    <div className="text-xs text-gray-600 mt-1">Seq: {processStep.sequenceOrder}</div>
                    {processStep.cycleTime > 0 && (
                      <div className="text-xs text-primary-600 mt-1">{processStep.cycleTime} min</div>
                    )}
                  </div>
                </div>
                {index < processFlow.processSteps.length - 1 && (
                  <ArrowRight className="h-5 w-5 text-primary-500 flex-shrink-0" />
                )}
              </React.Fragment>
            ))}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/bom')}
            className="border-primary-200 text-primary-700 hover:bg-primary-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Listing
          </Button>
          <div className="text-center space-y-2">
            <h1 className="bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
              {isCreateMode && 'BOM & Routing Management'}
              {isEditMode && 'Edit BOM & Routing'}
              {isViewMode && 'View BOM & Routing'}
            </h1>
            <p className="text-gray-600">
              {isCreateMode && 'Create and manage Bill of Materials with manufacturing routings'}
              {isEditMode && 'Modify existing Bill of Materials and routing information'}
              {isViewMode && 'View Bill of Materials and routing details'}
            </p>
          </div>
        </div>
      </div>

      {/* Loading State for Existing BOM Data */}
      {dataLoading && isEditOrViewMode && (
        <div className="text-center py-8 text-gray-500 bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-lg border border-primary-200">
          <Loader2 className="h-12 w-12 mx-auto text-primary-600 animate-spin mb-4" />
          <p className="text-lg font-medium mb-2">Loading BOM Data...</p>
          <p className="text-sm">Please wait while we fetch the BOM information.</p>
        </div>
      )}

      {/* BOM Basic Information */}
      <Card className="border-primary-200 shadow-lg shadow-primary-100/50">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
          <CardTitle className="flex items-center gap-2 text-primary-800">
            <FileText className="h-5 w-5 text-primary-700" />
            BOM Information
          </CardTitle>
          <CardDescription>
            Enter basic information about the Bill of Materials
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="bomName">BOM Name *</Label>
              <Input
                id="bomName"
                placeholder="Enter BOM name"
                value={bomName}
                onChange={(e) => setBomName(e.target.value)}
                disabled={isViewMode || isEditMode}
                className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endgood">Endgood Name</Label>
              <Select 
                value={selectedEndgood || ''} 
                onValueChange={(value) => {
                  setSelectedEndgood(value);
                  const endgood = endgoods.find(e => e.id.toString() === value);
                  setSelectedEndgoodData(endgood || null);
                }}
                disabled={isViewMode || isEditMode}
              >
                <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                  <SelectValue placeholder="Select endgood">
                    {selectedEndgoodData 
                      ? `${selectedEndgoodData.endgoodName} - ${selectedEndgoodData.partNumber}`
                      : selectedEndgood 
                        ? endgoods.find(e => e.id.toString() === selectedEndgood)?.endgoodName || 'Loading...'
                        : 'Select endgood'
                    }
                  </SelectValue>
                </SelectTrigger>
                <SelectContentWithSearch>
                  {loading ? (
                    <SelectItem value="loading" disabled>Loading endgoods...</SelectItem>
                  ) : (
                    endgoods.map((endgood) => (
                      <SelectItem key={endgood.id} value={endgood.id.toString()}>
                        {endgood.endgoodName} - {endgood.partNumber}
                      </SelectItem>
                    ))
                  )}
                </SelectContentWithSearch>
              </Select>
              {(selectedEndgood || selectedEndgoodData) && (
                <div className="mt-2 p-2 bg-primary-50 border border-primary-200 rounded-md">
                  <div className="text-sm text-primary-800">
                    <strong>Selected:</strong> {selectedEndgoodData?.endgoodName || endgoods.find(e => e.id.toString() === selectedEndgood)?.endgoodName || 'N/A'}
                  </div>
                  <div className="text-xs text-primary-600">
                    Part #: {selectedEndgoodData?.partNumber || endgoods.find(e => e.id.toString() === selectedEndgood)?.partNumber || 'N/A'} | 
                    SKU: {selectedEndgoodData?.sku || endgoods.find(e => e.id.toString() === selectedEndgood)?.sku || 'N/A'}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Enter BOM description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isViewMode || isEditMode}
                className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bomActiveStatus">BOM Status</Label>
              <Select
                value={bomIsActive ? 'active' : 'inactive'}
                onValueChange={(value) => setBomIsActive(value === 'active')}
                disabled={isViewMode}
              >
                <SelectTrigger id="bomActiveStatus" className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 disabled:bg-gray-100 disabled:cursor-not-allowed">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Mark this BOM as active or inactive for usage.
              </p>
              {isEditMode && (
                <div className="pt-2">
                  <Button
                    type="button"
                    onClick={handleSaveBomStatus}
                    disabled={loading}
                    className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg shadow-primary-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Saving...</span>
                      </div>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Status
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Routing Selection and Flow */}
      <Card className="border-primary-200 shadow-lg shadow-primary-100/50">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-primary-800">
                <FileText className="h-5 w-5 text-primary-700" />
                Manufacturing Routing
              </CardTitle>
              <CardDescription>
                Select or create a manufacturing routing for this BOM
              </CardDescription>
            </div>
            {(isEditMode || isViewMode) && selectedEndgood && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-primary-300 text-primary-700 hover:bg-primary-50 shrink-0"
                onClick={() => setIsProcessWeightDialogOpen(true)}
              >
                <Settings className="h-4 w-4 mr-2" />
                Process Weights
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          <div className="flex items-end gap-4">
            <div className="flex-1 space-y-2">
              <Label htmlFor="routing">Routing *</Label>
              {isViewMode || isEditMode ? (
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                  {selectedRoutingData ? (
                    <div>
                      <div className="font-medium text-gray-900">{selectedRoutingData.name}</div>
                      <div className="text-sm text-gray-600">{selectedRoutingData.processFlowNumber}</div>
                    </div>
                  ) : selectedRouting ? (
                    <div className="text-gray-500">Loading routing details...</div>
                  ) : (
                    <div className="text-gray-500">No routing selected</div>
                  )}
                </div>
              ) : (
                <Select 
                  value={selectedRouting} 
                  onValueChange={(value) => {
                    // console.log('Routing selected:', value);
                    // Clear previous routing data first
                    setSelectedRoutingData(null);
                    setSelectedRouting(value);
                    
                    // Only fetch if not loading existing data (to prevent duplicate fetches)
                    if (value && !isLoadingExistingData) {
                      // console.log('Fetching routing with steps for ID:', value);
                      fetchRoutingWithSteps(value);
                    } else if (value && isLoadingExistingData) {
                      // console.log('Skipping fetch - loading existing data');
                    } else {
                      // console.log('Clearing routing data');
                    }
                  }}
                >
                  <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                    <SelectValue placeholder="Select a routing" />
                  </SelectTrigger>
                  <SelectContentWithSearch>
                    {processFlowsLoading ? (
                      <SelectItem value="loading" disabled>
                        <div className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading routings...
                        </div>
                      </SelectItem>
                    ) : processFlows.length === 0 ? (
                      <SelectItem value="no-routings" disabled>
                        No routings available
                      </SelectItem>
                    ) : (
                      processFlows.map((routing) => (
                        <SelectItem key={routing.id} value={routing.id.toString()}>
                          {routing.name} - {routing.processFlowNumber}
                        </SelectItem>
                      ))
                    )}
                  </SelectContentWithSearch>
                </Select>
              )}
            </div>
            
            {!isViewMode && !isEditMode && (
              <Dialog open={isNewRoutingOpen} onOpenChange={setIsNewRoutingOpen}>
                <DialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="border-primary-300 text-primary-700 hover:bg-primary-50"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Routing
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Routing</DialogTitle>
                  <DialogDescription>
                    Define a new manufacturing routing with process sequence
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label>Routing Name *</Label>
                    <Input
                      value={newRoutingName}
                      onChange={(e) => setNewRoutingName(e.target.value)}
                      placeholder="Enter routing name"
                      className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">Process Sequence</h4>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          onClick={() => setIsNewProcessOpen(true)}
                          className="bg-green-600 text-white hover:bg-green-700"
                          size="sm"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Create New Process
                        </Button>
                        <Button
                          type="button"
                          onClick={addProcessToRouting}
                          className="bg-primary-500 text-gray-700 hover:bg-gray-50"
                          size="sm"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Process
                        </Button>
                      </div>
                    </div>

                    {routingProcesses.length > 0 && (
                      <div className="border border-primary-200 rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader className="bg-primary-50">
                            <TableRow>
                              <TableHead className="w-16">Seq</TableHead>
                              <TableHead>Process Name</TableHead>
                              <TableHead className="w-20">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {routingProcesses.map((process) => (
                              <TableRow key={process.id}>
                                <TableCell className="font-medium">{process.sequenceOrder}</TableCell>
                                <TableCell>
                                  <Select 
                                    value={process.processId} 
                                    onValueChange={(value) => updateRoutingProcess(process.id, 'processId', value)}
                                  >
                            <SelectTrigger className="border-primary-200">
                              <SelectValue placeholder="Select process" />
                            </SelectTrigger>
                            <SelectContentWithSearch>
                              {processesLoading ? (
                                <SelectItem value="loading" disabled>
                                  <div className="flex items-center gap-2">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Loading processes...
                                  </div>
                                </SelectItem>
                              ) : processes.length === 0 ? (
                                <SelectItem value="no-processes" disabled>
                                  No processes available
                                </SelectItem>
                              ) : (
                                processes.map((p) => (
                                  <SelectItem key={p.id} value={p.id.toString()}>
                                    <div>
                                      <div className="font-medium">{p.processName}</div>
                                      <div className="text-xs text-gray-500">{p.description}</div>
                                    </div>
                                  </SelectItem>
                                ))
                              )}
                            </SelectContentWithSearch>
                                  </Select>
                                </TableCell>
                                <TableCell>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => removeProcessFromRouting(process.id)}
                                    className="text-red-600 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-3">
                    <Button 
                      variant="outline" 
                      onClick={() => setIsNewRoutingOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={() => saveNewRouting()}
                      disabled={loading}
                      className="bg-primary-500 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      {loading ? 'Saving...' : 'Save Routing'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            )}
            
            {/* Create New Process Modal */}
            <Dialog open={isNewProcessOpen} onOpenChange={setIsNewProcessOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Create New Process</DialogTitle>
                  <DialogDescription>
                    Define a new manufacturing process
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="processName">Process Name *</Label>
                    <Input
                      id="processName"
                      value={newProcessName}
                      onChange={(e) => setNewProcessName(e.target.value)}
                      placeholder="Enter process name"
                      className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="processDescription">Description</Label>
                    <Textarea
                      id="processDescription"
                      value={newProcessDescription}
                      onChange={(e) => setNewProcessDescription(e.target.value)}
                      placeholder="Enter process description"
                      className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                      rows={3}
                    />
                  </div>
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setIsNewProcessOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={saveNewProcess}
                    disabled={creatingProcess}
                    className="bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    {creatingProcess ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Create Process
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Process Flow Diagram */}
          {routingLoading ? (
            <div className="p-4 bg-gradient-to-r from-primary-50/30 to-primary-100/30 rounded-lg border border-primary-200">
              <div className="flex items-center gap-2 mb-4">
                <Workflow className="h-5 w-5 text-primary-600" />
                <h4 className="font-medium text-primary-800">Process Flow</h4>
              </div>
              <div className="flex items-center justify-center p-8">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                  <span className="text-primary-600">Loading routing details...</span>
                </div>
              </div>
            </div>
          ) : selectedRoutingData && !routingLoading ? (
            <ProcessFlowDiagram key={`process-flow-${selectedRoutingData.id}`} processFlow={selectedRoutingData} />
          ) : selectedRouting && !routingLoading ? (
            <div className="p-4 bg-gradient-to-r from-primary-50/30 to-primary-100/30 rounded-lg border border-primary-200">
              <div className="flex items-center gap-2 mb-4">
                <Workflow className="h-5 w-5 text-primary-600" />
                <h4 className="font-medium text-primary-800">Process Flow</h4>
              </div>
              <div className="flex items-center justify-center p-8">
                <span className="text-primary-600">No process flow data available for selected routing</span>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <EndgoodProcessWeightDialog
        open={isProcessWeightDialogOpen}
        onOpenChange={setIsProcessWeightDialogOpen}
        endgoodId={selectedEndgood ?? ''}
        endgoodName={selectedEndgoodData?.endgoodName ?? ''}
        processSteps={selectedRoutingData?.processSteps ?? []}
        readOnly={isViewMode}
      />

      {/* Raw Materials Table */}
      <Card className="border-primary-200 shadow-lg shadow-primary-100/50">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
          <CardTitle className="flex items-center gap-2 text-primary-800">
            <FileText className="h-5 w-5 text-primary-700" />
            Raw Materials Used
          </CardTitle>
          <CardDescription>
            Define raw materials and consumables for each process
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              {isViewMode 
                ? 'Raw materials and consumables for each process'
                : 'Add materials required for each manufacturing process'
              }
            </p>
            {!isViewMode && (
              <Button
                onClick={addRawMaterialRow}
                className="bg-primary-500 text-gray-700 hover:bg-gray-50"
                size="sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Row
              </Button>
            )}
          </div>

          {rawMaterials.length > 0 ? (
            <div className="border border-primary-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                  <TableRow className="border-primary-200">
                    <TableHead>Process Name</TableHead>
                    <TableHead>Category of Item</TableHead>
                    <TableHead className="w-64">Item</TableHead>
                    <TableHead className="w-40">Quantity</TableHead>
                    <TableHead className="w-20">Unit</TableHead>
                    <TableHead className="w-24">Priority</TableHead>
                    {!isViewMode && <TableHead className="w-20">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rawMaterials.map((material) => {
                    const isReadOnlyMaterial = isViewMode || (isEditMode && !!material.bomDetailId);
                    return (
                    <TableRow key={material.id} className="border-primary-100">
                      <TableCell>
                        {isReadOnlyMaterial ? (
                          <span className="font-medium">{material.processName || 'N/A'}</span>
                        ) : (
                          <Select 
                            value={material.processName}
                            onValueChange={(value) => updateRawMaterial(material.id, 'processName', value)}
                            disabled={isEditMode && !!material.bomDetailId}
                          >
                            <SelectTrigger className="border-primary-200">
                              <SelectValue placeholder="Select process" />
                            </SelectTrigger>
                            <SelectContent>
                              {routingLoading ? (
                                <SelectItem value="loading" disabled>
                                  <div className="flex items-center gap-2">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Loading routing processes...
                                  </div>
                                </SelectItem>
                              ) : !selectedRoutingData || selectedRoutingData.processSteps.length === 0 ? (
                                <SelectItem value="no-processes" disabled>
                                  No routing selected or no processes in routing
                                </SelectItem>
                              ) : (
                                selectedRoutingData.processSteps.map((processStep) => {
                                  const processName = processStep.process.processName?.trim();
                                  const value = processName && processName.length > 0
                                    ? processName
                                    : `process-${processStep.process.id}`;
                                  return (
                                    <SelectItem key={processStep.process.id} value={value}>
                                      {processStep.process.processName}
                                    </SelectItem>
                                  );
                                })
                              )}
                            </SelectContent>
                          </Select>
                        )}
                      </TableCell>
                      <TableCell>
                        {isReadOnlyMaterial ? (
                          <span className="font-medium">{material.categoryOfItem || 'N/A'}</span>
                        ) : (
                          <Select 
                            value={material.categoryOfItem}
                            onValueChange={(value) => updateRawMaterial(material.id, 'categoryOfItem', value)}
                            disabled={isEditMode && !!material.bomDetailId}
                          >
                            <SelectTrigger className="border-primary-200">
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                            <SelectContentWithSearch>
                              {categoriesLoading ? (
                                <SelectItem value="loading" disabled>
                                  <div className="flex items-center gap-2">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Loading categories...
                                  </div>
                                </SelectItem>
                              ) : itemCategories.length === 0 ? (
                                <SelectItem value="no-categories" disabled>
                                  No categories available
                                </SelectItem>
                              ) : (
                                itemCategories.map((category) => {
                                  const name = category.categoryName?.trim() || '';
                                  const value = name.length > 0 ? name : `category-${category.id}`;
                                  return (
                                    <SelectItem key={category.id} value={value}>
                                      {category.categoryName}
                                    </SelectItem>
                                  );
                                })
                              )}
                            </SelectContentWithSearch>
                          </Select>
                        )}
                      </TableCell>
                      <TableCell>
                        {isReadOnlyMaterial ? (
                          <span className="font-medium">{material.item || 'N/A'}</span>
                        ) : (
                          <Select 
                            value={material.itemId != null ? material.itemId.toString() : ''}
                            onValueChange={(value) => updateRawMaterial(material.id, 'itemId', value)}
                            disabled={!material.categoryOfItem}
                          >
                            <SelectTrigger className="border-primary-200">
                              <SelectValue placeholder={material.categoryOfItem ? "Select item" : "Select category first"} />
                            </SelectTrigger>
                            <SelectContentWithSearch>
                              {rawItemsLoading ? (
                                <SelectItem value="loading" disabled>
                                  <div className="flex items-center gap-2">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Loading items...
                                  </div>
                                </SelectItem>
                              ) : (() => {
                                // Check if category is "Internal Manufacturing Part" to show endgoods
                                const isInternalCategory = isInternalManufacturingParts(material.categoryOfItem);
                                console.log('Item dropdown check:', {
                                  categoryOfItem: material.categoryOfItem,
                                  isInternalCategory,
                                  endgoodsCount: endgoods.length,
                                  rawItemsCount: rawItems.length
                                });
                                
                                if (isInternalCategory) {
                                  // Show all endgoods for "Internal Manufacturing Part" category
                                  if (endgoods.length === 0) {
                                    return (
                                      <SelectItem value="no-endgoods" disabled>
                                        No endgoods available
                                      </SelectItem>
                                    );
                                  }
                                  
                                  return endgoods.map((endgood) => (
                                    <SelectItem key={endgood.id} value={endgood.id.toString()}>
                                      {endgood.endgoodName}
                                    </SelectItem>
                                  ));
                                } else {
                                  // Show raw items for other categories (match category case-insensitively)
                                  const materialCategory = (material.categoryOfItem || '').trim().toLowerCase();
                                  const filteredItems = rawItems.filter(item => {
                                    if (!materialCategory) return true;
                                    const itemCategoryName = item.itemCategory?.categoryName
                                      ? item.itemCategory.categoryName.trim().toLowerCase()
                                      : '';
                                    return itemCategoryName === materialCategory;
                                  });

                                  if (filteredItems.length === 0) {
                                    return (
                                      <SelectItem value="no-items" disabled>
                                        {material.categoryOfItem 
                                          ? `No items available for ${material.categoryOfItem}`
                                          : 'Please select a category first'
                                        }
                                      </SelectItem>
                                    );
                                  }
                                  
                                  return filteredItems.map((item) => (
                                    <SelectItem key={item.id} value={item.id.toString()}>
                                      {item.itemName}
                                    </SelectItem>
                                  ));
                                }
                              })()}
                            </SelectContentWithSearch>
                          </Select>
                        )}
                      </TableCell>
                      <TableCell className="w-40">
                        {isReadOnlyMaterial ? (
                          <span className="font-medium">{material.quantity || 'N/A'}</span>
                        ) : (
                          <QuantityInput
                            value={material.quantity}
                            onChange={(value) => updateRawMaterial(material.id, 'quantity', value)}
                          />
                        )}
                      </TableCell>
                      <TableCell className="w-20">
                        {isReadOnlyMaterial ? (
                          <span className="font-medium text-sm">{material.unit || 'N/A'}</span>
                        ) : (
                          <Input
                            value={material.unit || ''}
                            disabled={true}
                            placeholder="Auto-populated"
                            className="border-primary-200 bg-gray-50 text-gray-600 w-full text-sm"
                          />
                        )}
                      </TableCell>
                      <TableCell className="w-24">
                        {isReadOnlyMaterial ? (
                          <span className="font-medium">{material.priority ?? 'N/A'}</span>
                        ) : (
                          <Input
                            type="number"
                            value={material.priority ?? ''}
                            onChange={(e) => updateRawMaterial(material.id, 'priority', e.target.value ? parseInt(e.target.value) : undefined)}
                            placeholder="Priority"
                            className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 w-full"
                            min="1"
                          />
                        )}
                      </TableCell>
                      {!isViewMode && (
                        <TableCell>
                          <div className="flex gap-2">
                            {isEditMode && !material.bomDetailId && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => saveNewBomDetail(material.id)}
                                disabled={loading}
                                className="text-green-600 hover:bg-green-50 border-green-200"
                                title="Save this entry"
                              >
                                <Save className="h-4 w-4" />
                              </Button>
                            )}
                            {(!isEditMode || !material.bomDetailId) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => removeRawMaterial(material.id)}
                                className="text-red-600 hover:bg-red-50 border-red-200"
                                title="Delete entry"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  )})}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-lg border border-primary-200">
              <Layers className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-lg font-medium mb-2">No Raw Materials</p>
              <p className="text-sm">
                {isViewMode 
                  ? 'This BOM does not have any raw materials defined.'
                  : 'Add materials required for each manufacturing process.'
                }
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons - Only show in create mode */}
      {isCreateMode && (
        <div className="flex justify-end gap-4 pt-6">
          <Button 
            variant="outline" 
            onClick={handleCancel}
            className="border-primary-200 text-primary-700 hover:bg-primary-50 hover:border-primary-300"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={loading}
            className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg shadow-primary-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Saving...</span>
              </div>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Create BOM
              </>
            )}
          </Button>
        </div>
      )}

    </div>
  );
}